import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Popover,
  IconButton,
  Snackbar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  PendingActions,
  EventAvailable,
  Group,
  ThumbUp,
  ThumbDown,
  Print as PrintIcon,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { leaveService, PendingApproval } from "../../services/leaveService";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import LeaveStatementReport from "../reports/LeaveStatementReport";
import ActionConfirmationDialog from "../common/ActionConfirmationDialog";

interface ApprovalHistory {
  id: number;
  action: string;
  comments: string;
  created_at: string;
  approver_name: string;
  approver_role: string;
  next_approver_name: string | null;
  next_approver_role: string | null;
}

const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>(
    [],
  );
  const [approvalHistory, setApprovalHistory] = useState<ApprovalHistory[]>([]);
  const [teamStats, setTeamStats] = useState({
    totalTeamMembers: 0,
    pendingApprovals: 0,
    approvedThisMonth: 0,
  });
  const [selectedApplication, setSelectedApplication] =
    useState<PendingApproval | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [approvalAction, setApprovalAction] = useState<string>("");
  const [selectedAttachments, setSelectedAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [substituteRequests, setSubstituteRequests] = useState<any[]>([]);
  const [processingSub, setProcessingSub] = useState<number | null>(null);
  const [uiNotification, setUiNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "info" });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    actionLabel: string;
    actionColor: "success" | "error" | "warning" | "primary" | "info";
    action: string | null;
    summary: { label: string; value: string | number }[];
  }>({
    open: false,
    title: "",
    actionLabel: "",
    actionColor: "primary",
    action: null,
    summary: [],
  });

  // Team management states
  const [teamCalendarOpen, setTeamCalendarOpen] = useState(false);
  const [teamLeavesOpen, setTeamLeavesOpen] = useState(false);
  const [teamBalanceOpen, setTeamBalanceOpen] = useState(false);
  const [leaveStatementOpen, setLeaveStatementOpen] = useState(false);
  const [teamCalendar, setTeamCalendar] = useState<any>({});
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [teamLeaves, setTeamLeaves] = useState<any[]>([]);
  const [teamLeavesPagination, setTeamLeavesPagination] = useState({
    page: 1,
    totalPages: 1,
    perPage: 50,
  });
  const [teamBalance, setTeamBalance] = useState<any[]>([]);
  const [balanceLeaveTypes, setBalanceLeaveTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedBalanceLeaveTypeIds, setSelectedBalanceLeaveTypeIds] =
    useState<number[]>([]);

  // Leave-type summary states
  const [leaveSummaryOpen, setLeaveSummaryOpen] = useState(false);
  const [leaveSummaryLoading, setLeaveSummaryLoading] = useState(false);
  const [leaveSummaryMonth, setLeaveSummaryMonth] = useState(
    new Date().getMonth() + 1,
  );
  const [leaveSummaryYear, setLeaveSummaryYear] = useState(
    new Date().getFullYear(),
  );
  const [leaveSummaryData, setLeaveSummaryData] = useState<{
    summary: any[];
    totals: { totalDays: number; totalApplications: number };
  }>({ summary: [], totals: { totalDays: 0, totalApplications: 0 } });

  // Calendar popover state (for team calendar dialog)
  const [calendarPopover, setCalendarPopover] = useState<{
    anchorEl: HTMLElement | null;
    date: string;
    approved: any[];
    pending: any[];
  }>({ anchorEl: null, date: "", approved: [], pending: [] });

  useEffect(() => {
    loadManagerData();

    const onFocus = () => loadManagerData();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(loadManagerData, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, []);

  const loadManagerData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch pending approvals
      const approvals = await leaveService.getPendingApprovals();
      setPendingApprovals(approvals);
      console.log("Pending approvals:", approvals);

      // Fetch substitute requests
      const subRequests = await leaveService.getPendingSubstituteRequests();
      setSubstituteRequests(subRequests);

      // Fetch manager stats
      const statsResponse = await api.get("/manager/stats");

      const stats = statsResponse.data;
      console.log("Manager stats:", stats);
      setTeamStats(stats);
      setError(""); // Clear any previous errors
    } catch (error) {
      console.error("Failed to load manager data:", error);
      // Don't show error to user since we have fallback data
      // Set fallback stats on error
      setTeamStats({
        totalTeamMembers: 0,
        pendingApprovals: pendingApprovals.length,
        approvedThisMonth: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAct = async (application: PendingApproval) => {
    // Re-check current state before opening the dialog to avoid acting on stale data
    try {
      const response = await api.get(`/leaves/${application.id}/details`);
      const leave = response.data.leave;
      const stillActionable =
        leave &&
        ["pending", "cancellation_pending"].includes(leave.status) &&
        Number(leave.current_approver_id) === Number(user?.id);

      if (!stillActionable) {
        setPendingApprovals((prev) =>
          prev.filter((app) => app.id !== application.id),
        );
        setUiNotification({
          open: true,
          message:
            "This application was already actioned or is no longer assigned to you. The list has been refreshed.",
          severity: "warning",
        });
        await loadManagerData();
        return;
      }

      setSelectedApplication(application);
      setApprovalDialogOpen(true);
      setSelectedAttachments([]);
      setApprovalHistory(response.data.approvalHistory || []);
      setSelectedAttachments(response.data.leave?.attachments || []);
    } catch (err) {
      console.error("Failed to load approval history:", err);
      setSelectedApplication(application);
      setApprovalDialogOpen(true);
      setSelectedAttachments([]);
      setApprovalHistory([]);
    }
  };

  const handleProcessAction = (action: string) => {
    if (!selectedApplication) return;

    let title = "";
    let actionLabel = "";
    let actionColor: any = "primary";

    const isCancellation =
      selectedApplication.status === "cancellation_pending";

    switch (action) {
      case "approved":
        title = isCancellation
          ? "Approve Cancellation"
          : "Approve Leave Application";
        actionLabel = isCancellation ? "Approve Cancellation" : "Approve";
        actionColor = "success";
        break;
      case "rejected":
        title = isCancellation
          ? "Reject Cancellation"
          : "Reject Leave Application";
        actionLabel = isCancellation ? "Reject Cancellation" : "Reject";
        actionColor = "error";
        break;
      case "recommend":
        title = "Recommend Leave Application";
        actionLabel = "Recommend";
        actionColor = "primary";
        break;
      case "forward":
        title = "Forward Leave Application";
        actionLabel = "Forward";
        actionColor = "info";
        break;
      case "route_next":
        title = "Route To Next Approver";
        actionLabel = "Send To Next Approver";
        actionColor = "info";
        break;
      case "route_previous":
        title = "Route To Previous Approver";
        actionLabel = "Send To Previous Approver";
        actionColor = "warning";
        break;
      case "route_applicant":
        title = "Route To Applicant";
        actionLabel = "Send To Applicant";
        actionColor = "warning";
        break;
      case "return":
        title = "Return Leave Application";
        actionLabel = "Return";
        actionColor = "warning";
        break;
    }

    setConfirmDialog({
      open: true,
      title,
      actionLabel,
      actionColor,
      action,
      summary: [
        { label: "Employee", value: selectedApplication.employee_name },
        { label: "Leave Type", value: selectedApplication.leave_type },
        { label: "Days", value: selectedApplication.number_of_days },
        { label: "Comments", value: comments || "(None)" },
      ],
    });
  };

  const processApproval = async () => {
    if (!selectedApplication || !confirmDialog.action) return;

    if (
      ["route_next", "route_previous", "route_applicant"].includes(
        confirmDialog.action,
      ) &&
      !comments.trim()
    ) {
      setError("Comments are required when routing an application.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        applicationId: selectedApplication.id,
        action: confirmDialog.action,
        comments,
      };

      await api.post("/approvals/process", payload);

      setPendingApprovals((prev) =>
        prev.filter((app) => app.id !== selectedApplication.id),
      );

      setTeamStats((prev) => ({
        ...prev,
        pendingApprovals: prev.pendingApprovals - 1,
      }));

      setConfirmDialog({ ...confirmDialog, open: false });
      setApprovalDialogOpen(false);
      setSelectedApplication(null);
      setComments("");
      setApprovalHistory([]);
      setError("");
      setUiNotification({
        open: true,
        message: `Application ${confirmDialog.action.replace("_", " ")} completed successfully.`,
        severity: "success",
      });

      await loadManagerData();
    } catch (error) {
      console.error("Failed to process approval:", error);
      setError("Failed to process approval. Please try again.");
      setUiNotification({
        open: true,
        message: "Failed to process approval. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubstituteAction = async (
    id: number,
    action: "accept" | "reject",
  ) => {
    try {
      setProcessingSub(id);
      await leaveService.processSubstituteAction(id, action);
      await loadManagerData();
      setUiNotification({
        open: true,
        message:
          action === "accept"
            ? "Substitute request accepted."
            : "Substitute request rejected.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to process substitute action:", error);
      setError("Failed to process substitute action");
      setUiNotification({
        open: true,
        message: "Failed to process substitute action.",
        severity: "error",
      });
    } finally {
      setProcessingSub(null);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: {
      [key: string]:
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning";
    } = {
      pending: "warning",
      approved: "success",
      rejected: "error",
      cancellation_pending: "info",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  // Team management functions
  const handleViewTeamCalendar = async (month?: number, year?: number) => {
    const targetMonth = month !== undefined ? month : calendarMonth;
    const targetYear = year !== undefined ? year : calendarYear;
    try {
      setLoading(true);
      const response = await api.get(
        `/manager/calendar?month=${targetMonth + 1}&year=${targetYear}`,
      );
      setTeamCalendar(response.data.calendar);
      setCalendarMonth(targetMonth);
      setCalendarYear(targetYear);
      setTeamCalendarOpen(true);
    } catch (error) {
      console.error("Failed to load team calendar:", error);
      // Set empty calendar on error
      setTeamCalendar({});
      setCalendarMonth(targetMonth);
      setCalendarYear(targetYear);
      setTeamCalendarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCalendarPrevMonth = () => {
    let newMonth = calendarMonth - 1;
    let newYear = calendarYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    }
    handleViewTeamCalendar(newMonth, newYear);
  };

  const handleCalendarNextMonth = () => {
    let newMonth = calendarMonth + 1;
    let newYear = calendarYear;
    if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    handleViewTeamCalendar(newMonth, newYear);
  };

  const handleViewLeaveSummary = async (month?: number, year?: number) => {
    const targetMonth = month !== undefined ? month : leaveSummaryMonth;
    const targetYear = year !== undefined ? year : leaveSummaryYear;
    try {
      setLeaveSummaryLoading(true);
      const response = await api.get(
        `/manager/leave-type-summary?month=${targetMonth}&year=${targetYear}`,
      );
      setLeaveSummaryData({
        summary: response.data.summary || [],
        totals: response.data.totals || {
          totalDays: 0,
          totalApplications: 0,
        },
      });
      setLeaveSummaryMonth(targetMonth);
      setLeaveSummaryYear(targetYear);
      setLeaveSummaryOpen(true);
    } catch (error) {
      console.error("Failed to load leave type summary:", error);
      setLeaveSummaryData({
        summary: [],
        totals: { totalDays: 0, totalApplications: 0 },
      });
      setLeaveSummaryOpen(true);
    } finally {
      setLeaveSummaryLoading(false);
    }
  };

  const handleViewTeamLeaves = async (page: number = 1) => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const response = await api.get(
        `/manager/team-leaves?year=${currentYear}`,
      );
      const allLeaves = response.data;

      // Simple pagination on client side
      const perPage = teamLeavesPagination.perPage;
      const totalPages = Math.ceil(allLeaves.length / perPage);
      const startIdx = (page - 1) * perPage;
      const endIdx = startIdx + perPage;
      const pageLeaves = allLeaves.slice(startIdx, endIdx);

      setTeamLeaves(pageLeaves);
      setTeamLeavesPagination({ page, totalPages, perPage });
      setTeamLeavesOpen(true);
    } catch (error) {
      console.error("Failed to load team leaves:", error);
      setTeamLeaves([]);
      setTeamLeavesOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTeamBalance = async (leaveTypeIds: number[] = []) => {
    try {
      setLoading(true);
      if (balanceLeaveTypes.length === 0) {
        try {
          const types = await leaveService.getLeaveTypes();
          setBalanceLeaveTypes(
            (types || []).map((t: any) => ({ id: t.id, name: t.name })),
          );
        } catch (typeErr) {
          console.error("Failed to load leave types:", typeErr);
        }
      }
      const url =
        leaveTypeIds.length > 0
          ? `/manager/team-leave-balance?leaveTypeIds=${leaveTypeIds.join(",")}`
          : "/manager/team-leave-balance";
      const response = await api.get(url);
      setTeamBalance(response.data);
      setTeamBalanceOpen(true);
    } catch (error) {
      console.error("Failed to load team balance:", error);
      // Set empty balance data on error
      setTeamBalance([]);
      setTeamBalanceOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceLeaveTypeFilterChange = (ids: number[]) => {
    setSelectedBalanceLeaveTypeIds(ids);
    handleViewTeamBalance(ids);
  };

  if (loading && pendingApprovals.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading manager dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={uiNotification.open}
        autoHideDuration={3000}
        onClose={() => setUiNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={uiNotification.severity}
          variant="filled"
          onClose={() =>
            setUiNotification((prev) => ({ ...prev, open: false }))
          }
          sx={{ width: "100%" }}
        >
          {uiNotification.message}
        </Alert>
      </Snackbar>

      {/* Manager Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Group color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {teamStats.totalTeamMembers}
                  </Typography>
                  <Typography color="textSecondary">Staff Members</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PendingActions color="warning" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {teamStats.pendingApprovals}
                  </Typography>
                  <Typography color="textSecondary">
                    Pending Approvals
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <EventAvailable color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {teamStats.approvedThisMonth}
                  </Typography>
                  <Typography color="textSecondary">
                    Approved This Month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Substitute Requests */}
      {substituteRequests.length > 0 && (
        <Card sx={{ mb: 4, border: "2px solid #ed6c02" }}>
          <CardContent>
            <Typography
              variant="h6"
              color="warning.main"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PendingActions sx={{ mr: 1 }} />
              Substitute Requests (Action Required)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              The following employees have requested you to be their substitute.
              They cannot proceed for approval until you accept.
            </Typography>
            {substituteRequests.map((req) => (
              <Box
                key={req.id}
                sx={{
                  p: 2,
                  mb: 1,
                  backgroundColor: "#fffdf0",
                  borderRadius: 1,
                  border: "1px solid #ffe58f",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1">
                    <strong>{req.employee_name}</strong> ({req.leave_type})
                  </Typography>
                  <Typography variant="body2">
                    {new Date(req.start_date).toLocaleDateString()} -{" "}
                    {new Date(req.end_date).toLocaleDateString()} (
                    {req.number_of_days} days)
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => handleSubstituteAction(req.id, "reject")}
                    disabled={processingSub === req.id}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleSubstituteAction(req.id, "accept")}
                    disabled={processingSub === req.id}
                  >
                    Accept
                  </Button>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <PendingActions sx={{ mr: 1 }} />
            Pending Approvals ({pendingApprovals.length})
          </Typography>

          {pendingApprovals.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
              No pending approvals.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Application #</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Leave Type</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingApprovals.map((application) => (
                    <TableRow key={application.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {application.application_number}
                        </Typography>
                        {application.status === "cancellation_pending" && (
                          <Chip
                            label="CANCELLATION"
                            size="small"
                            color="info"
                            sx={{ mt: 0.5, fontSize: "0.65rem", height: 20 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {application.employee_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {application.employee_code} •{" "}
                            {application.department_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={application.leave_type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(
                            application.start_date,
                          ).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          to{" "}
                          {new Date(application.end_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {application.number_of_days} day
                          {application.number_of_days !== 1 ? "s" : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={application.reason}
                        >
                          {application.reason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleAct(application)}
                          disabled={loading}
                          sx={{ minWidth: 100 }}
                        >
                          Act
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleViewTeamLeaves()}
              disabled={loading}
            >
              View Team Leaves
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleViewTeamCalendar()}
              disabled={loading}
            >
              View Team Calendar
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleViewTeamBalance()}
              disabled={loading}
            >
              Team Leave Balance
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleViewLeaveSummary()}
              disabled={loading}
            >
              Monthly Leave Type Summary
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setLeaveStatementOpen(true)}
              disabled={loading}
              startIcon={<PrintIcon />}
            >
              Monthly Leave Statement
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedApplication && (
            <>
              Process Leave Application -{" "}
              {selectedApplication.application_number}
              <Typography variant="subtitle2" color="textSecondary">
                {selectedApplication.employee_name} (
                {selectedApplication.employee_code})
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mb: 2 }}>
              {selectedApplication.status === "cancellation_pending" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This is a <strong>cancellation request</strong> for a
                  previously approved leave.
                  {selectedApplication.cancellation_reason && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Cancellation reason: "
                      {selectedApplication.cancellation_reason}"
                    </Typography>
                  )}
                </Alert>
              )}
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Leave Type:</strong> {selectedApplication.leave_type}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Duration:</strong>{" "}
                {new Date(selectedApplication.start_date).toLocaleDateString()}{" "}
                to {new Date(selectedApplication.end_date).toLocaleDateString()}{" "}
                ({selectedApplication.number_of_days} day
                {selectedApplication.number_of_days !== 1 ? "s" : ""})
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Reason:</strong> {selectedApplication.reason}
              </Typography>
            </Box>
          )}

          {selectedAttachments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments:
              </Typography>
              {selectedAttachments.map((attachment: any) => (
                <Box key={attachment.id} sx={{ mb: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        const apiUrl =
                          process.env.REACT_APP_API_URL ||
                          "http://localhost:5002/api";
                        const response = await fetch(
                          `${apiUrl}/attachments/${attachment.id}`,
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          },
                        );
                        if (!response.ok) {
                          throw new Error("Failed to download attachment");
                        }
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.style.display = "none";
                        a.href = url;
                        a.download = attachment.original_filename;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (err) {
                        console.error("Download error:", err);
                        alert("Failed to download attachment");
                      }
                    }}
                    sx={{ mr: 1 }}
                  >
                    Download {attachment.original_filename}
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    ({(attachment.size / 1024).toFixed(1)} KB)
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {approvalHistory.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Approval History:
              </Typography>
              <Box sx={{ pl: 1 }}>
                {approvalHistory.map((history, index) => {
                  let actionText = "";
                  if (history.action === "substitute_approve") {
                    actionText = `accepted by substitute ${history.approver_name}`;
                  } else if (history.action === "substitute_reject") {
                    actionText = `rejected by substitute ${history.approver_name}`;
                  } else if (
                    history.action === "forward" ||
                    history.action === "recommend"
                  ) {
                    actionText = `${history.action}ed by ${
                      history.approver_name
                    } (${history.approver_role})${
                      history.next_approver_name
                        ? ` to ${history.next_approver_name} (${history.next_approver_role})`
                        : ""
                    }`;
                  } else {
                    actionText = `${history.action}${
                      history.action.endsWith("e") ? "d" : "ed"
                    } by ${history.approver_name} (${history.approver_role})`;
                  }

                  return (
                    <Box key={history.id} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>
                          •{" "}
                          {actionText.charAt(0).toUpperCase() +
                            actionText.slice(1)}
                        </strong>
                        {history.comments && (
                          <span> - "{history.comments}"</span>
                        )}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(history.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (Optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments for the employee..."
            disabled={loading}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: 2 }}>
          <Button
            onClick={() => setApprovalDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          {selectedApplication?.status !== "cancellation_pending" && (
            <>
              <Button
                onClick={() => handleProcessAction("route_applicant")}
                color="warning"
                variant="outlined"
                disabled={loading}
                size="small"
              >
                Send To Applicant
              </Button>
              <Button
                onClick={() => handleProcessAction("route_previous")}
                color="warning"
                variant="outlined"
                disabled={loading}
                size="small"
              >
                Send To Previous
              </Button>
              <Button
                onClick={() => handleProcessAction("route_next")}
                color="info"
                variant="outlined"
                disabled={loading}
                size="small"
              >
                Send To Next
              </Button>
            </>
          )}
          <Button
            onClick={() => handleProcessAction("rejected")}
            color="error"
            variant="outlined"
            disabled={loading}
            startIcon={<ThumbDown />}
          >
            {selectedApplication?.status === "cancellation_pending"
              ? "Reject Cancellation"
              : "Reject"}
          </Button>
          {selectedApplication?.action_type === "recommend" ? (
            <>
              <Button
                onClick={() => handleProcessAction("recommend")}
                color="primary"
                variant="outlined"
                disabled={loading}
                startIcon={<ThumbUp />}
              >
                Recommend
              </Button>
              <Button
                onClick={() => handleProcessAction("forward")}
                color="info"
                variant="outlined"
                disabled={loading}
              >
                Forward
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleProcessAction("approved")}
              color="success"
              variant="contained"
              disabled={loading}
              startIcon={<ThumbUp />}
            >
              {selectedApplication?.status === "cancellation_pending"
                ? "Approve Cancellation"
                : "Approve"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ActionConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        actionLabel={confirmDialog.actionLabel}
        actionColor={confirmDialog.actionColor}
        summary={confirmDialog.summary}
        onConfirm={processApproval}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={loading}
      />

      {/* Team Calendar Dialog */}
      <Dialog
        open={teamCalendarOpen}
        onClose={() => setTeamCalendarOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={handleCalendarPrevMonth} size="small">
              <NavigateBefore />
            </IconButton>
            <Typography variant="h6">
              Team Leave Calendar -{" "}
              {new Date(calendarYear, calendarMonth).toLocaleDateString(
                "en-US",
                {
                  month: "long",
                  year: "numeric",
                },
              )}
            </Typography>
            <IconButton onClick={handleCalendarNextMonth} size="small">
              <NavigateNext />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Click on a day to see employee details
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
            }}
          >
            {/* Day headers */}
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <Box
                key={day}
                sx={{
                  p: 1,
                  textAlign: "center",
                  fontWeight: "bold",
                  bgcolor: "#f5f5f5",
                }}
              >
                {day}
              </Box>
            ))}

            {/* Calendar days */}
            {(() => {
              const year = calendarYear;
              const month = calendarMonth;
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const startDate = new Date(firstDay);
              startDate.setDate(startDate.getDate() - firstDay.getDay());

              const days = [];
              const current = new Date(startDate);

              while (current <= lastDay || days.length % 7 !== 0) {
                const dateStr = `${current.getFullYear()}-${String(
                  current.getMonth() + 1,
                ).padStart(2, "0")}-${String(current.getDate()).padStart(
                  2,
                  "0",
                )}`;
                const dayData = teamCalendar[dateStr] || {
                  approved: 0,
                  pending: 0,
                  employees: [],
                };
                const isCurrentMonth = current.getMonth() === month;
                const currentDateCopy = new Date(current);

                days.push(
                  <Box
                    key={dateStr}
                    sx={{
                      p: 1,
                      minHeight: 80,
                      border: "1px solid #e0e0e0",
                      bgcolor: isCurrentMonth ? "white" : "#f9f9f9",
                      cursor:
                        dayData.approved > 0 || dayData.pending > 0
                          ? "pointer"
                          : "default",
                      "&:hover": {
                        bgcolor:
                          dayData.approved > 0 || dayData.pending > 0
                            ? "#e3f2fd"
                            : "inherit",
                      },
                    }}
                    onClick={(event) => {
                      if (dayData.employees && dayData.employees.length > 0) {
                        // Show employee details using popover
                        setCalendarPopover({
                          anchorEl: event.currentTarget as HTMLElement,
                          date: dateStr,
                          approved: dayData.employees.filter(
                            (e: any) => e.status === "approved",
                          ),
                          pending: dayData.employees.filter(
                            (e: any) => e.status === "pending",
                          ),
                        });
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", mb: 0.5 }}
                    >
                      {currentDateCopy.getDate()}
                    </Typography>
                    {dayData.approved > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "success.main",
                            borderRadius: "50%",
                          }}
                        />
                        <Typography variant="caption" color="success.main">
                          {dayData.approved} approved
                        </Typography>
                      </Box>
                    )}
                    {dayData.pending > 0 && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "warning.main",
                            borderRadius: "50%",
                          }}
                        />
                        <Typography variant="caption" color="warning.main">
                          {dayData.pending} pending
                        </Typography>
                      </Box>
                    )}
                  </Box>,
                );

                current.setDate(current.getDate() + 1);
              }

              return days;
            })()}
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Legend:
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "success.main",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="caption">Approved Leave</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "warning.main",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="caption">Pending Application</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamCalendarOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Team Leave Balance Dialog */}
      <Dialog
        open={teamBalanceOpen}
        onClose={() => setTeamBalanceOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Team Leave Balance</DialogTitle>
        <DialogContent>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", mb: 2 }}
          >
            Note: Leave balances are provisional and subject to change based on
            leaves taken this year.
          </Typography>
          <FormControl sx={{ minWidth: 280, mb: 2 }} size="small">
            <InputLabel id="balance-leave-type-filter-label">
              Filter by Leave Type
            </InputLabel>
            <Select
              labelId="balance-leave-type-filter-label"
              multiple
              value={selectedBalanceLeaveTypeIds}
              onChange={(e) =>
                handleBalanceLeaveTypeFilterChange(e.target.value as number[])
              }
              input={<OutlinedInput label="Filter by Leave Type" />}
              renderValue={(selected) =>
                (selected as number[])
                  .map(
                    (id) =>
                      balanceLeaveTypes.find((lt) => lt.id === id)?.name ?? id,
                  )
                  .join(", ") || "All Leave Types"
              }
            >
              {balanceLeaveTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  <Checkbox
                    checked={selectedBalanceLeaveTypeIds.includes(lt.id)}
                  />
                  <ListItemText primary={lt.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Department</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Used</TableCell>
                  <TableCell align="right">Available</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamBalance.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No staff members found
                    </TableCell>
                  </TableRow>
                ) : (
                  teamBalance.map((balance: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{balance.department_name}</TableCell>
                      <TableCell>{balance.name}</TableCell>
                      <TableCell>{balance.employee_code}</TableCell>
                      <TableCell>{balance.leave_type}</TableCell>
                      <TableCell align="right">
                        {balance.total_entitlement}
                      </TableCell>
                      <TableCell align="right">{balance.used_days}</TableCell>
                      <TableCell align="right">
                        {balance.available_days}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamBalanceOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Team Leaves Dialog */}
      <Dialog
        open={teamLeavesOpen}
        onClose={() => setTeamLeavesOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Team Leave Applications</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>App #</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applied Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No leave applications found
                    </TableCell>
                  </TableRow>
                ) : (
                  teamLeaves.map((leave: any) => (
                    <TableRow
                      key={leave.id}
                      hover
                      onClick={() => navigate(`/leaves/${leave.id}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{leave.application_number}</TableCell>
                      <TableCell>{leave.department_name}</TableCell>
                      <TableCell>{leave.employee_name}</TableCell>
                      <TableCell>{leave.employee_code}</TableCell>
                      <TableCell>{leave.leave_type}</TableCell>
                      <TableCell>
                        {new Date(leave.start_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(leave.end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        {leave.number_of_days}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={leave.status}
                          color={getStatusColor(leave.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(leave.applied_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leaves/${leave.id}`);
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {teamLeavesPagination.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  disabled={teamLeavesPagination.page === 1}
                  onClick={() =>
                    handleViewTeamLeaves(teamLeavesPagination.page - 1)
                  }
                >
                  Previous
                </Button>
                <Typography sx={{ alignSelf: "center", mx: 2 }}>
                  Page {teamLeavesPagination.page} of{" "}
                  {teamLeavesPagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={
                    teamLeavesPagination.page ===
                    teamLeavesPagination.totalPages
                  }
                  onClick={() =>
                    handleViewTeamLeaves(teamLeavesPagination.page + 1)
                  }
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamLeavesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Monthly Leave Type Summary Dialog */}
      <Dialog
        open={leaveSummaryOpen}
        onClose={() => setLeaveSummaryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Monthly Leave Type Summary</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 1 }}>
            <TextField
              select
              label="Month"
              size="small"
              value={leaveSummaryMonth}
              onChange={(e) =>
                handleViewLeaveSummary(Number(e.target.value), leaveSummaryYear)
              }
              sx={{ minWidth: 150 }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <MenuItem key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString("en-US", {
                    month: "long",
                  })}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              size="small"
              value={leaveSummaryYear}
              onChange={(e) =>
                handleViewLeaveSummary(
                  leaveSummaryMonth,
                  Number(e.target.value),
                )
              }
              sx={{ minWidth: 120 }}
            >
              {Array.from(
                { length: 6 },
                (_, i) => new Date().getFullYear() - i,
              ).map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {leaveSummaryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : leaveSummaryData.summary.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
              No leave applications found for this month.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Leave Type</TableCell>
                    <TableCell align="right">Applications</TableCell>
                    <TableCell align="right">Employees</TableCell>
                    <TableCell align="right">Approved (Days)</TableCell>
                    <TableCell align="right">Pending (Days)</TableCell>
                    <TableCell align="right">Total Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveSummaryData.summary.map((item) => (
                    <TableRow key={item.leaveTypeId} hover>
                      <TableCell>{item.leaveType}</TableCell>
                      <TableCell align="right">
                        {item.totalApplications}
                      </TableCell>
                      <TableCell align="right">{item.employeeCount}</TableCell>
                      <TableCell align="right">
                        {item.byStatus?.approved?.days || 0}
                      </TableCell>
                      <TableCell align="right">
                        {item.byStatus?.pending?.days || 0}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{item.totalDays}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        {leaveSummaryData.totals.totalApplications}
                      </strong>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="right">
                      <strong>{leaveSummaryData.totals.totalDays}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Monthly Leave Statement Dialog */}
      <Dialog
        open={leaveStatementOpen}
        onClose={() => setLeaveStatementOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Monthly Leave Statement</DialogTitle>
        <DialogContent>
          <LeaveStatementReport onClose={() => setLeaveStatementOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Calendar Day Popover */}
      <Popover
        open={Boolean(calendarPopover.anchorEl)}
        anchorEl={calendarPopover.anchorEl}
        onClose={() =>
          setCalendarPopover({
            anchorEl: null,
            date: "",
            approved: [],
            pending: [],
          })
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: 1.5, borderBottom: "1px solid #e0e0e0", pb: 1 }}
          >
            {calendarPopover.date &&
              new Date(calendarPopover.date + "T00:00:00").toLocaleDateString(
                "en-US",
                {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )}
          </Typography>

          {calendarPopover.approved.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase" }}
              >
                Approved Leaves ({calendarPopover.approved.length})
              </Typography>
              {calendarPopover.approved.map((emp: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: "#f6ffed",
                    borderRadius: 1,
                    borderLeft: "3px solid #52c41a",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#d9f7be" },
                  }}
                  onClick={() => {
                    // Navigate only if we have a leave application id
                    if (!emp.leave_id) return;
                    setCalendarPopover({
                      anchorEl: null,
                      date: "",
                      approved: [],
                      pending: [],
                    });
                    window.location.href = `/leaves/${emp.leave_id}`;
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {emp.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {emp.leave_type} • {emp.department || ""} •{" "}
                    {emp.number_of_days || 1} day(s)
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {calendarPopover.pending.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase" }}
              >
                Pending Leaves ({calendarPopover.pending.length})
              </Typography>
              {calendarPopover.pending.map((emp: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: "#fffbe6",
                    borderRadius: 1,
                    borderLeft: "3px solid #faad14",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#fff1b8" },
                  }}
                  onClick={() => {
                    if (!emp.leave_id) return;
                    setCalendarPopover({
                      anchorEl: null,
                      date: "",
                      approved: [],
                      pending: [],
                    });
                    window.location.href = `/leaves/${emp.leave_id}`;
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {emp.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {emp.leave_type} • {emp.department || ""} •{" "}
                    {emp.number_of_days || 1} day(s)
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {calendarPopover.approved.length === 0 &&
            calendarPopover.pending.length === 0 && (
              <Typography color="textSecondary" align="center">
                No leaves on this day
              </Typography>
            )}
        </Box>
      </Popover>
    </Box>
  );
};

export default ManagerDashboard;
